import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Timer,
  Alarm,
  CheckCircle,
  PlayArrow,
  Pause,
  Stop
} from '@mui/icons-material';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { getTimeRemaining, getTaskUrgency } from '../utils/celebrationHelper';

function TaskTimer({ task, onComplete, compact = false }) {
  const [openTimer, setOpenTimer] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [estimatedMinutes] = useState(task?.estimatedTime || 30);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerKey, setTimerKey] = useState(0);

  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const startTimer = () => {
    setTimerRunning(true);
    setOpenTimer(true);
  };

  const pauseTimer = () => {
    setTimerRunning(false);
  };

  const stopTimer = () => {
    setTimerRunning(false);
    setElapsedSeconds(0);
    setTimerKey(prev => prev + 1);
  };

  const completeTask = () => {
    setTimerRunning(false);
    if (onComplete) {
      onComplete({
        timeSpent: elapsedSeconds,
        estimatedTime: estimatedMinutes * 60
      });
    }
    setOpenTimer(false);
    stopTimer();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeDisplay = ({ remainingTime }) => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;

    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
          {minutes}:{seconds.toString().padStart(2, '0')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          remaining
        </Typography>
      </Box>
    );
  };

  // Get urgency for tasks with due dates
  const urgency = task?.dueDate ? getTaskUrgency(task.dueDate) : 'normal';
  const timeRemaining = task?.dueDate ? getTimeRemaining(task.dueDate) : null;

  const urgencyColors = {
    normal: 'primary',
    soon: 'warning',
    urgent: 'error',
    overdue: 'error'
  };

  if (compact) {
    return (
      <>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Timer />}
          onClick={startTimer}
          color={urgencyColors[urgency]}
        >
          Start Task
        </Button>

        {/* Timer Dialog */}
        <Dialog 
          open={openTimer} 
          onClose={() => !timerRunning && setOpenTimer(false)}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            <Timer sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5">{task?.title || 'Task Timer'}</Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Countdown Timer */}
              <CountdownCircleTimer
                key={timerKey}
                isPlaying={timerRunning}
                duration={estimatedMinutes * 60}
                colors={['#4caf50', '#ff9800', '#f44336', '#f44336']}
                colorsTime={[estimatedMinutes * 60, estimatedMinutes * 30, estimatedMinutes * 10, 0]}
                size={220}
                strokeWidth={12}
                onComplete={() => {
                  // Timer finished!
                  return { shouldRepeat: false };
                }}
              >
                {formatTimeDisplay}
              </CountdownCircleTimer>

              {/* Elapsed Time */}
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Time Elapsed
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {formatTime(elapsedSeconds)}
                </Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((elapsedSeconds / (estimatedMinutes * 60)) * 100, 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Timer Controls */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', width: '100%' }}>
                {!timerRunning ? (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrow />}
                    onClick={() => setTimerRunning(true)}
                    fullWidth
                  >
                    Start
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Pause />}
                    onClick={pauseTimer}
                    color="warning"
                    fullWidth
                  >
                    Pause
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Stop />}
                  onClick={stopTimer}
                  color="error"
                >
                  Reset
                </Button>
              </Box>

              {/* Complete Task Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={completeTask}
                color="success"
                fullWidth
                sx={{ mt: 2 }}
              >
                Complete Task
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTimer(false)} disabled={timerRunning}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Full display with due date info
  return (
    <Box sx={{ mt: 2 }}>
      {timeRemaining && (
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<Alarm />}
            label={
              timeRemaining.overdue
                ? 'Overdue'
                : `Due in ${timeRemaining.hours}h ${timeRemaining.minutes}m`
            }
            color={urgencyColors[urgency]}
            size="small"
          />
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<Timer />}
        onClick={startTimer}
        fullWidth
        size="large"
        color={urgencyColors[urgency]}
      >
        Start Task Timer
      </Button>

      {/* Timer Dialog (same as compact mode) */}
      <Dialog 
        open={openTimer} 
        onClose={() => !timerRunning && setOpenTimer(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Timer sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5">{task?.title || 'Task Timer'}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CountdownCircleTimer
              key={timerKey}
              isPlaying={timerRunning}
              duration={estimatedMinutes * 60}
              colors={['#4caf50', '#ff9800', '#f44336', '#f44336']}
              colorsTime={[estimatedMinutes * 60, estimatedMinutes * 30, estimatedMinutes * 10, 0]}
              size={220}
              strokeWidth={12}
            >
              {formatTimeDisplay}
            </CountdownCircleTimer>

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Time Elapsed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatTime(elapsedSeconds)}
              </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min((elapsedSeconds / (estimatedMinutes * 60)) * 100, 100)}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', width: '100%' }}>
              {!timerRunning ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={() => setTimerRunning(true)}
                  fullWidth
                >
                  Start
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Pause />}
                  onClick={pauseTimer}
                  color="warning"
                  fullWidth
                >
                  Pause
                </Button>
              )}
              <Button
                variant="outlined"
                size="large"
                startIcon={<Stop />}
                onClick={stopTimer}
                color="error"
              >
                Reset
              </Button>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<CheckCircle />}
              onClick={completeTask}
              color="success"
              fullWidth
              sx={{ mt: 2 }}
            >
              Complete Task
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTimer(false)} disabled={timerRunning}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaskTimer;

